<script setup lang="ts">
/**
 * Page.vue - Payment Page Wrapper
 *
 * This component acts as a wrapper that:
 * 1. Detects if the request comes from a custom domain or base domain
 * 2. Loads the appropriate hotel/merchant configuration
 * 3. Loads the correct template based on the configuration
 * 4. Delegates all business logic to PageCore
 * 5. Renders the dynamic template with all necessary props
 *
 * This architecture allows for:
 * - Easy addition of new custom templates
 * - Separation of business logic from presentation
 * - Custom branding per merchant via custom domains
 */

import { ref, onMounted, onUnmounted, nextTick } from 'vue';
import { decodeLinkParams } from '$utils/decodeLinkParams';
import logger from '$utils/logger';
import { useHotelConfig } from '../composables/useHotelConfig';
import { useTemplateLoader } from '../composables/useTemplateLoader';
import { createPageCore } from './PageCore';
import { getConfig } from '../config/environment';

// Initialize composables
const { hotelConfig: apiHotelConfig, loadConfig } = useHotelConfig();
const { currentTemplate, loadPageTemplate } = useTemplateLoader();

// Initialize page core (business logic)
const core = createPageCore();

// Local state for detecting domain type
const isCustomDomain = ref(false);
const isLoading = ref(true);
const loadError = ref<string | null>(null);

/**
 * Detects if the current hostname is a custom domain or the base domain
 */
const detectDomainType = (): { isCustom: boolean; identifier: string | null } => {
    const hostname = window.location.hostname;
    const envConfig = getConfig();
    // Use validDomains from environment config
    const baseDomains = envConfig.validDomains;

    // Check if it's a base domain
    const isBaseDomain = baseDomains.some((domain) => hostname === domain || hostname.endsWith(`.${domain}`));

    if (isBaseDomain) {
        // Base domain - extract slug from path
        const pathSegments = window.location.pathname.split('/').filter(Boolean);
        const slug = pathSegments.pop(); // Get last segment as slug
        return { isCustom: false, identifier: slug || null };
    } else {
        // Custom domain - use full hostname as identifier
        return { isCustom: true, identifier: hostname };
    }
};

/**
 * Closes language menu when clicking outside
 */
const closeLanguageMenu = (event: MouseEvent) => {
    const target = event.target as Element;
    const languageMenu = document.getElementById('language-menu-button');
    const languageDropdown = document.querySelector('[role="menu"]');
    if (
        languageMenu &&
        languageDropdown &&
        !languageMenu.contains(target) &&
        !languageDropdown.contains(target) &&
        core.isLanguageMenuOpen.value
    ) {
        core.isLanguageMenuOpen.value = false;
    }
};

/**
 * Main initialization function
 */
onMounted(async () => {
    document.addEventListener('click', closeLanguageMenu);

    try {
        // 1. Decode URL parameters
        const decoded = decodeLinkParams(window.location.href);

        if (!decoded) {
            loadError.value = 'Invalid payment link parameters';
            core.paymentStatus.value = 'error';
            isLoading.value = false;
            return;
        }

        core.params.value = decoded as any;

        // 2. Detect domain type and get identifier
        const { isCustom, identifier } = detectDomainType();
        isCustomDomain.value = isCustom;

        logger.info('Page.vue: Domain detection', {
            isCustomDomain: isCustom,
            identifier,
            hostname: window.location.hostname,
            pathname: window.location.pathname,
        });

        if (!identifier) {
            loadError.value = 'Could not determine merchant identifier';
            core.paymentStatus.value = 'error';
            isLoading.value = false;
            return;
        }

        // 3. Determine page mode (hotel vs merchant)
        const pathSegments = window.location.pathname.split('/').filter(Boolean);
        const mode = pathSegments.includes('merchant') ? 'merchant' : 'hotel';
        core.pageMode.value = mode;

        logger.info('Page.vue: Page mode detected', { mode });

        // 4. Load hotel/merchant configuration
        logger.info(`Page.vue: Loading configuration for identifier: ${identifier}`);
        const configLoaded = await loadConfig(identifier);

        if (!configLoaded || !apiHotelConfig.value) {
            loadError.value = 'Failed to load merchant configuration';
            core.paymentStatus.value = 'error';
            isLoading.value = false;
            return;
        }

        logger.info('Page.vue: Configuration loaded', {
            name: apiHotelConfig.value.name,
            slug: apiHotelConfig.value.slug,
            templateName: apiHotelConfig.value.templateName,
            customDomain: apiHotelConfig.value.customDomain,
        });

        // 5. Determine which template to load
        const templateName = apiHotelConfig.value.templateName || 'default';
        logger.info(`Page.vue: Loading template: ${templateName}`);

        const templateLoaded = await loadPageTemplate(templateName);

        if (!templateLoaded) {
            logger.warn(`Page.vue: Failed to load template '${templateName}', using default`);
            await loadPageTemplate('default');
        }

        // 6. Initialize page core with hotel config (but don't wait for SDK rendering)
        // Set isLoading = false first so the template renders and creates the container
        isLoading.value = false;

        // Wait for Vue to update the DOM before initializing SDK
        await nextTick();
        logger.info('Page.vue: Template rendered, now initializing SDK...');

        // Now initialize core which will load the SDK and render into the container
        await core.initialize(apiHotelConfig.value);
        logger.info('Page.vue: Initialization complete');
    } catch (error: any) {
        logger.error('Page.vue: Error during initialization:', error);
        loadError.value = error.message || 'Unknown error during initialization';
        core.paymentStatus.value = 'error';
        isLoading.value = false;
    }
});

onUnmounted(() => {
    document.removeEventListener('click', closeLanguageMenu);
});

// Helper methods for two-way binding with template
const handleUpdateCustomerInfo = (field: string, value: string) => {
    (core.customerInfo as any)[field] = value;
};

const handleUpdateBillingAddress = (field: string, value: string) => {
    (core.billingAddress as any)[field] = value;
};

const handleUpdateInvoiceData = (field: string, value: string) => {
    (core.invoiceData as any)[field] = value;
};
</script>

<template>
    <!-- Loading State -->
    <div v-if="isLoading" class="flex items-center justify-center min-h-screen bg-gray-100">
        <div class="text-center">
            <div class="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-blue-600 mx-auto mb-4"></div>
            <p class="text-gray-600 text-lg">{{ core.t('loading') }}</p>
        </div>
    </div>

    <!-- Error State -->
    <div v-else-if="loadError" class="flex items-center justify-center min-h-screen bg-gray-100">
        <div class="bg-white rounded-lg shadow-md p-8 max-w-md">
            <div class="text-center">
                <svg class="mx-auto h-16 w-16 text-red-500 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    ></path>
                </svg>
                <h2 class="text-2xl font-bold text-gray-900 mb-2">Error</h2>
                <p class="text-gray-600">{{ loadError }}</p>
            </div>
        </div>
    </div>

    <!-- Render Dynamic Template -->
    <component
        v-else-if="currentTemplate && core.hotelConfig.value"
        :is="currentTemplate"
        :payment-status="core.paymentStatus.value"
        :is-card-form-valid="core.isCardFormValid.value"
        :is-policies-accepted="core.isPoliciesAccepted.value"
        @update:is-policies-accepted="core.isPoliciesAccepted.value = $event"
        :hotel-config="core.hotelConfig.value"
        :language="core.language.value"
        :requires-invoice="core.requiresInvoice.value"
        @update:requires-invoice="core.requiresInvoice.value = $event"
        :is-sdk-ready="core.isSdkReady.value"
        :confirmation-code="core.confirmationCode.value"
        :payment-timestamp="core.paymentTimestamp.value"
        :copied="core.copied.value"
        :page-mode="core.pageMode.value"
        :is-language-menu-open="core.isLanguageMenuOpen.value"
        @update:is-language-menu-open="core.isLanguageMenuOpen.value = $event"
        :rfc-error="core.rfcError.value"
        :billing-country-display="core.billingCountryDisplay.value"
        @update:billing-country-display="core.billingCountryDisplay.value = $event"
        :booking-summary="core.bookingSummary"
        :customer-info="core.customerInfo"
        @update:customer-info="handleUpdateCustomerInfo"
        :billing-address="core.billingAddress"
        @update:billing-address="handleUpdateBillingAddress"
        :invoice-data="core.invoiceData"
        @update:invoice-data="handleUpdateInvoiceData"
        :params="core.params.value"
        :contact-info="core.contactInfo.value"
        :privacy-url="core.privacyUrl.value"
        :terms-url="core.termsUrl.value"
        :has-privacy-or-terms-urls="core.hasPrivacyOrTermsUrls.value"
        :can-proceed-with-payment="core.canProceedWithPayment.value"
        :countries="core.countries.value"
        :t="core.t"
        :format-currency="core.formatCurrency"
        :format-date="core.formatDate"
        :set-language="core.setLanguage"
        :handle-rfc-validation="core.handleRfcValidation"
        :copy-confirmation-code="core.copyConfirmationCode"
        :handle-form-submit="core.handleFormSubmit"
    />
</template>
